import PropTypes from 'prop-types';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import NumberFormat from 'react-number-format';

import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import { hotelSearch, updateHotelSearchObj } from '../../../../../store/slices/hotel';
import { hotelSearchRequest, setHotelTripSearchInputs } from '../../../../../store/slices/tripSearch';
import Filter from '../../../../Filters/Filter';
import TwSlider from '../../../../TwSlider';
import getHotelPriceDisplayPreference from '../../../../../helpers/selectors/getHotelPriceDisplayPreference';
import { HotelRateDisplayPreferenceLabels } from '../../../../../constants';
import currencySymbol from '../../../../../helpers/currencySymbol';

const HotelPriceFilter = ({ tripId, fontSize, filterSpacingClass }) => {
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isTripSearch = !tripId;
  const hotelRateDisplayPreference = useSelector(getHotelPriceDisplayPreference());
  const hotelSearchObj = useSelector((store) => (!isTripSearch
    ? (store.hotel.hotelSearches[tripId] || {}).searchObj || {}
    : store.tripSearch.searchInputs.hotel));

  const [displaySliderValue, setDisplaySliderValue] = useState([0, 0]);

  const searchState = useSelector((store) => (!isTripSearch
    ? (store.hotel.hotelSearches[tripId] || {}) : store.tripSearch.searchResult
    && (store.tripSearch.searchResult.filters || {})));

  const isEmpty = (!hotelSearchObj.fromPrice || hotelSearchObj.fromPrice === searchState.minPrice)
    && (!hotelSearchObj.toPrice || hotelSearchObj.toPrice === searchState.maxPrice);

  const handleDisplayPriceChange = (event, newValue) => {
    setDisplaySliderValue(newValue);
  };

  const search = useCallback((event, value, doSearch) => {
    const fromPrice = !value ? undefined : value[0];
    const toPrice = !value ? undefined : value[1];
    const payload = {
      tripId,
      searchObj: {
        fromPrice,
        toPrice,
        page: 1,
        hasFilterApplied: true,
      },
    };

    let doTrack = false;
    if (isTripSearch) {
      if (hotelSearchObj.fromPrice !== undefined || fromPrice !== undefined
        || hotelSearchObj.toPrice !== undefined || toPrice !== undefined) {
        dispatch(setHotelTripSearchInputs({ ...payload.searchObj }));
        dispatch(hotelSearchRequest(payload.searchObj));
        doTrack = true;
      }
    } else {
      if (hotelSearchObj.fromPrice !== undefined || fromPrice !== undefined
        || hotelSearchObj.toPrice !== undefined || toPrice !== undefined) {
        dispatch(updateHotelSearchObj(payload)); doTrack = true;
      }
      if (doSearch) {
        dispatch(hotelSearch(payload)); doTrack = true;
      }
    }
    if (doTrack) {
      track('hotelPriceFilter.handleChange', {
        filterHotelChains: JSON.stringify(0),
      });
    }
  }, [dispatch, hotelSearchObj.fromPrice, hotelSearchObj.toPrice, isTripSearch, track, tripId]);

  const handleClear = () => {
    search(undefined, [searchState.minPrice, searchState.maxPrice], true);
    track('hotelPriceFilter.handleClear');
  };

  const formatNumber = useCallback((value) => (
    <NumberFormat value={value} displayType="text" thousandSeparator prefix={currencySymbol(searchState && searchState.currencyCode)} />
  ), [searchState]);

  const fromPrice = useMemo(() => formatNumber(displaySliderValue[0]),
    [displaySliderValue, formatNumber]);
  const toPrice = useMemo(() => formatNumber(displaySliderValue[1]),
    [displaySliderValue, formatNumber]);

  useEffect(() => {
    if (searchState.minPrice && searchState.maxPrice) {
      if (hotelSearchObj.fromPrice && hotelSearchObj.toPrice
        && hotelSearchObj.fromPrice >= searchState.minPrice
        && hotelSearchObj.fromPrice <= searchState.maxPrice
        && hotelSearchObj.toPrice >= searchState.minPrice
        && hotelSearchObj.toPrice <= searchState.maxPrice) {
        setDisplaySliderValue([hotelSearchObj.fromPrice, hotelSearchObj.toPrice]);
      } else {
        search(null, undefined, false);
        setDisplaySliderValue([searchState.minPrice, searchState.maxPrice]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelSearchObj.fromPrice, hotelSearchObj.toPrice,
    searchState.maxPrice, searchState.minPrice]);

  const filterTitle = useMemo(() => {
    if (hotelRateDisplayPreference >= 0) {
      return HotelRateDisplayPreferenceLabels[hotelRateDisplayPreference].label;
    }
    return HotelRateDisplayPreferenceLabels[0].label;
  }, [hotelRateDisplayPreference]);

  return searchState && searchState.minPrice && searchState.maxPrice ? (
    <Filter
      label={filterTitle}
      menuWidthClass="w-max"
      isFilterEmpty={isEmpty}
      variant="outline"
      filterMaxWidthClass="max-w-full"
      handleClear={handleClear}
      fontSize={fontSize}
      filterSpacingClass={filterSpacingClass}
      bgClassName={!isTripSearch ? '' : undefined}
    >
      <div className="px-5 pt-4 w-64">
        <div className="grid grid-cols-3 mb-1">
          <div className="text-sm text-gray-700 whitespace-no-wrap">
            {filterTitle}
            :
          </div>
          <div className="col-span-2 text-right text-xs text-gray-700">
            {fromPrice}
            {' '}
            -
            {' '}
            {toPrice}
          </div>
        </div>
        <TwSlider
          value={displaySliderValue}
          onChange={handleDisplayPriceChange}
          onChangeCommitted={(event, value) => search(event, value, true)}
          min={(searchState && searchState.minPrice) || 0}
          max={(searchState && searchState.maxPrice) || 0}
        />
      </div>
    </Filter>
  ) : <></>;
};

HotelPriceFilter.propTypes = {
  tripId: PropTypes.string,
  fontSize: PropTypes.string,
  filterSpacingClass: PropTypes.string,
};

HotelPriceFilter.defaultProps = {
  tripId: undefined,
  fontSize: undefined,
  filterSpacingClass: undefined,
};

export default HotelPriceFilter;
